import { useState, useEffect } from "react";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "@/integrations/firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { logError } from "@/shared/services/ErrorReporting";

interface SummaryChunk {
  chunk_index: number;
  paragraphs: string[];
}

function extractParagraphsFromChunks(summaryChunks: SummaryChunk[]): string[] {
  if (!summaryChunks || summaryChunks.length === 0) {
    return [];
  }

  return [...summaryChunks]
    .sort((a, b) => a.chunk_index - b.chunk_index)
    .flatMap((chunk) => chunk.paragraphs ?? [])
    .filter((paragraph) => paragraph.trim().length > 0);
}

export interface UseParagraphHistoriesResult {
  paragraphs: string[];
  isLoading: boolean;
  error: Error | null;
}

export const useParagraphHistories = (
  summaryId: string | null,
): UseParagraphHistoriesResult => {
  const { authUser } = useAuth();
  const [paragraphs, setParagraphs] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!authUser || !summaryId) {
      setParagraphs([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);

    const paragraphHistoriesQuery = query(
      collection(db, "summaries", summaryId, "paragraph_histories"),
      orderBy("revision", "desc"),
      limit(1),
    );

    const unsubscribe = onSnapshot(
      paragraphHistoriesQuery,
      (snapshot) => {
        if (snapshot.empty) {
          setParagraphs([]);
          setIsLoading(false);
          return;
        }

        const latestRevisionDoc = snapshot.docs[0];
        const summaryChunks = (latestRevisionDoc.data().summary_chunks ||
          []) as SummaryChunk[];
        setParagraphs(extractParagraphsFromChunks(summaryChunks));
        setIsLoading(false);
      },
      (err) => {
        logError(err);
        setError(err);
        setParagraphs([]);
        setIsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [authUser, summaryId]);

  return { paragraphs, isLoading, error };
};
